import type { RoomType, RoomConfig, RoomConversionOption, ProjectTypeConfig, SelectionCategoryConfig } from '@/types/firestore';
import {
  ChefHat,
  Bath,
  Home,
  Sofa,
  Bed,
  Trees,
  Car,
  Laptop,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

export interface RoomTypeOption {
  id: RoomType;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const ROOM_TYPES: RoomTypeOption[] = [
  {
    id: 'kitchen',
    label: 'Kitchen',
    description: 'Cabinets, countertops, flooring',
    icon: ChefHat,
  },
  {
    id: 'bathroom',
    label: 'Bathroom',
    description: 'Vanity, tile, fixtures',
    icon: Bath,
  },
  {
    id: 'basement',
    label: 'Basement',
    description: 'Full finishing or refresh',
    icon: Home,
  },
  {
    id: 'living_room',
    label: 'Living Room',
    description: 'Flooring, walls, built-ins',
    icon: Sofa,
  },
  {
    id: 'bedroom',
    label: 'Bedroom',
    description: 'Closets, flooring, paint',
    icon: Bed,
  },
  {
    id: 'outdoor',
    label: 'Outdoor/Deck',
    description: 'Decking, railings, pergola',
    icon: Trees,
  },
  {
    id: 'garage',
    label: 'Garage',
    description: 'Flooring, storage, organization',
    icon: Car,
  },
  {
    id: 'home_office',
    label: 'Home Office',
    description: 'Built-ins, flooring, lighting',
    icon: Laptop,
  },
  {
    id: 'other',
    label: 'Other',
    description: 'Custom room or space',
    icon: HelpCircle,
  },
];

// Selection options per category
export interface SelectionOption {
  id: string;
  label: string;
  description?: string;
}

export interface SelectionCategory {
  id: string;
  label: string;
  options: SelectionOption[];
  allowColor?: boolean;
}

// Flooring options
export const FLOORING_OPTIONS: SelectionCategory = {
  id: 'flooring',
  label: 'Flooring',
  options: [
    { id: 'hardwood', label: 'Hardwood', description: 'Classic wood flooring' },
    { id: 'laminate', label: 'Laminate', description: 'Affordable wood look' },
    { id: 'tile', label: 'Tile', description: 'Ceramic or porcelain' },
    { id: 'vinyl', label: 'Vinyl/LVP', description: 'Waterproof luxury vinyl' },
    { id: 'carpet', label: 'Carpet', description: 'Soft and comfortable' },
    { id: 'concrete', label: 'Polished Concrete', description: 'Modern industrial' },
  ],
  allowColor: true,
};

// Wall options
export const WALL_OPTIONS: SelectionCategory = {
  id: 'walls',
  label: 'Walls',
  options: [
    { id: 'paint', label: 'Fresh Paint', description: 'New color or refresh' },
    { id: 'wallpaper', label: 'Wallpaper', description: 'Patterns and textures' },
    { id: 'tile', label: 'Accent Tile', description: 'Backsplash or feature wall' },
    { id: 'paneling', label: 'Wall Paneling', description: 'Shiplap or wainscoting' },
  ],
  allowColor: true,
};

// Cabinet options
export const CABINET_OPTIONS: SelectionCategory = {
  id: 'cabinets',
  label: 'Cabinets',
  options: [
    { id: 'refinish', label: 'Refinish/Paint', description: 'Update existing cabinets' },
    { id: 'replace', label: 'Full Replacement', description: 'New cabinet installation' },
    { id: 'reface', label: 'Reface Doors', description: 'New doors, keep boxes' },
  ],
  allowColor: true,
};

// Countertop options
export const COUNTERTOP_OPTIONS: SelectionCategory = {
  id: 'countertops',
  label: 'Countertops',
  options: [
    { id: 'laminate', label: 'Laminate', description: 'Budget-friendly' },
    { id: 'quartz', label: 'Quartz', description: 'Engineered stone' },
    { id: 'granite', label: 'Granite', description: 'Natural stone' },
    { id: 'butcherblock', label: 'Butcher Block', description: 'Warm wood surface' },
    { id: 'marble', label: 'Marble', description: 'Luxury natural stone' },
  ],
};

// Lighting options
export const LIGHTING_OPTIONS: SelectionCategory = {
  id: 'lighting',
  label: 'Lighting',
  options: [
    { id: 'basic', label: 'Update Fixtures', description: 'Replace existing lights' },
    { id: 'recessed', label: 'Recessed Lighting', description: 'Can lights installation' },
    { id: 'custom', label: 'Custom Design', description: 'Pendants, chandeliers, etc.' },
  ],
};

// Fixture options (bathroom specific)
export const FIXTURE_OPTIONS: SelectionCategory = {
  id: 'fixtures',
  label: 'Fixtures',
  options: [
    { id: 'standard', label: 'Standard Update', description: 'Replace faucets, hardware' },
    { id: 'premium', label: 'Premium Upgrade', description: 'High-end fixtures' },
  ],
};

// Vanity options (bathroom specific)
export const VANITY_OPTIONS: SelectionCategory = {
  id: 'vanity',
  label: 'Vanity',
  options: [
    { id: 'refinish', label: 'Refinish', description: 'Paint or stain existing' },
    { id: 'replace', label: 'Replace', description: 'New vanity installation' },
  ],
};

// Shower/Tub options
export const SHOWER_OPTIONS: SelectionCategory = {
  id: 'shower',
  label: 'Shower/Tub',
  options: [
    { id: 'retile', label: 'Retile', description: 'New tile surrounds' },
    { id: 'insert', label: 'Insert/Liner', description: 'Acrylic surround' },
    { id: 'walkin', label: 'Walk-in Shower', description: 'Glass enclosure conversion' },
  ],
};

// Get available selections based on room type
export function getSelectionsForRoom(roomType: RoomType): SelectionCategory[] {
  switch (roomType) {
    case 'kitchen':
      return [FLOORING_OPTIONS, WALL_OPTIONS, CABINET_OPTIONS, COUNTERTOP_OPTIONS, LIGHTING_OPTIONS];
    case 'bathroom':
      return [FLOORING_OPTIONS, WALL_OPTIONS, VANITY_OPTIONS, SHOWER_OPTIONS, FIXTURE_OPTIONS, LIGHTING_OPTIONS];
    case 'basement':
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    case 'living_room':
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    case 'bedroom':
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    case 'outdoor':
      return [FLOORING_OPTIONS, LIGHTING_OPTIONS];
    case 'garage':
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    case 'home_office':
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    case 'other':
      // Basic selections for custom/other room types
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
    default:
      return [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS];
  }
}

// ============================================
// OUTDOOR PROJECT TYPE SELECTION CATEGORIES
// ============================================

// Deck selections
export const DECK_MATERIAL_OPTIONS: SelectionCategory = {
  id: 'decking_material',
  label: 'Decking Material',
  options: [
    { id: 'wood', label: 'Natural Wood', description: 'Cedar, redwood, or pressure-treated' },
    { id: 'composite', label: 'Composite', description: 'Low-maintenance wood alternative' },
    { id: 'pvc', label: 'PVC/Vinyl', description: 'Fully synthetic, waterproof' },
  ],
};

export const RAILING_OPTIONS: SelectionCategory = {
  id: 'railing_style',
  label: 'Railing Style',
  options: [
    { id: 'none', label: 'No Railing', description: 'Open deck design' },
    { id: 'wood', label: 'Wood Railing', description: 'Traditional wood posts and balusters' },
    { id: 'metal', label: 'Metal Railing', description: 'Aluminum or steel' },
    { id: 'cable', label: 'Cable Railing', description: 'Modern horizontal cables' },
    { id: 'glass', label: 'Glass Panels', description: 'Unobstructed views' },
  ],
};

// Patio selections
export const PATIO_SURFACE_OPTIONS: SelectionCategory = {
  id: 'surface_material',
  label: 'Surface Material',
  options: [
    { id: 'pavers', label: 'Pavers', description: 'Interlocking brick or stone' },
    { id: 'concrete', label: 'Poured Concrete', description: 'Smooth or brushed finish' },
    { id: 'stamped', label: 'Stamped Concrete', description: 'Decorative patterns' },
    { id: 'flagstone', label: 'Flagstone', description: 'Natural irregular stone' },
  ],
};

export const BORDER_OPTIONS: SelectionCategory = {
  id: 'border_treatment',
  label: 'Border/Edge',
  options: [
    { id: 'none', label: 'No Border', description: 'Clean edge to lawn' },
    { id: 'contrasting', label: 'Contrasting Border', description: 'Different material edge' },
    { id: 'raised', label: 'Raised Edge', description: 'Low wall or curb' },
  ],
};

// Pergola selections
export const PERGOLA_STRUCTURE_OPTIONS: SelectionCategory = {
  id: 'structure_material',
  label: 'Structure Material',
  options: [
    { id: 'wood', label: 'Wood', description: 'Cedar, redwood, or pine' },
    { id: 'vinyl', label: 'Vinyl', description: 'Low-maintenance white' },
    { id: 'aluminum', label: 'Aluminum', description: 'Modern, powder-coated' },
    { id: 'fiberglass', label: 'Fiberglass', description: 'Durable, paintable' },
  ],
};

export const CANOPY_OPTIONS: SelectionCategory = {
  id: 'canopy_shade',
  label: 'Canopy/Shade',
  options: [
    { id: 'none', label: 'Open Top', description: 'Traditional lattice only' },
    { id: 'fabric', label: 'Fabric Canopy', description: 'Retractable shade cloth' },
    { id: 'louvered', label: 'Louvered Roof', description: 'Adjustable slats' },
    { id: 'solid', label: 'Solid Roof', description: 'Full weather protection' },
  ],
};

// Pool Area selections
export const POOL_DECKING_OPTIONS: SelectionCategory = {
  id: 'pool_decking',
  label: 'Pool Decking/Coping',
  options: [
    { id: 'concrete', label: 'Stamped Concrete', description: 'Cool deck finish' },
    { id: 'pavers', label: 'Pavers', description: 'Non-slip stone or brick' },
    { id: 'travertine', label: 'Travertine', description: 'Natural stone, stays cool' },
    { id: 'composite', label: 'Composite Decking', description: 'Wood-look, splinter-free' },
  ],
};

export const POOL_FENCING_OPTIONS: SelectionCategory = {
  id: 'pool_fencing',
  label: 'Pool Fencing',
  options: [
    { id: 'glass', label: 'Glass Panels', description: 'Frameless or semi-frameless' },
    { id: 'aluminum', label: 'Aluminum', description: 'Classic pool fence' },
    { id: 'mesh', label: 'Mesh Safety', description: 'Removable safety fence' },
    { id: 'wrought', label: 'Wrought Iron', description: 'Decorative metal' },
  ],
};

// Gazebo selections
export const GAZEBO_STRUCTURE_OPTIONS: SelectionCategory = {
  id: 'gazebo_structure',
  label: 'Structure Material',
  options: [
    { id: 'wood', label: 'Wood', description: 'Traditional cedar or pine' },
    { id: 'vinyl', label: 'Vinyl', description: 'Maintenance-free' },
    { id: 'metal', label: 'Metal', description: 'Steel or aluminum frame' },
  ],
};

export const SCREENING_OPTIONS: SelectionCategory = {
  id: 'screening',
  label: 'Screening',
  options: [
    { id: 'none', label: 'Open Air', description: 'No screens' },
    { id: 'screen', label: 'Screen Panels', description: 'Bug protection' },
    { id: 'curtains', label: 'Curtains', description: 'Privacy and shade' },
    { id: 'glass', label: 'Glass Enclosure', description: 'Year-round use' },
  ],
};

// Outdoor Kitchen selections
export const OUTDOOR_COUNTERTOP_OPTIONS: SelectionCategory = {
  id: 'outdoor_countertop',
  label: 'Countertop Material',
  options: [
    { id: 'granite', label: 'Granite', description: 'Natural stone, heat-resistant' },
    { id: 'concrete', label: 'Concrete', description: 'Custom shapes, durable' },
    { id: 'tile', label: 'Tile', description: 'Ceramic or porcelain' },
    { id: 'stainless', label: 'Stainless Steel', description: 'Professional look' },
  ],
};

export const APPLIANCE_OPTIONS: SelectionCategory = {
  id: 'appliance_package',
  label: 'Appliance Package',
  options: [
    { id: 'basic', label: 'Basic', description: 'Grill and prep area' },
    { id: 'standard', label: 'Standard', description: 'Grill, sink, refrigerator' },
    { id: 'premium', label: 'Premium', description: 'Full kitchen with pizza oven' },
  ],
};

// Fire Pit selections
export const FIRE_TYPE_OPTIONS: SelectionCategory = {
  id: 'fire_type',
  label: 'Fire Type',
  options: [
    { id: 'wood', label: 'Wood Burning', description: 'Traditional campfire feel' },
    { id: 'gas', label: 'Natural Gas', description: 'Convenient, clean burning' },
    { id: 'propane', label: 'Propane', description: 'Portable, easy setup' },
  ],
};

export const SEATING_OPTIONS: SelectionCategory = {
  id: 'seating_style',
  label: 'Seating Style',
  options: [
    { id: 'built_in', label: 'Built-in Seating', description: 'Stone or concrete bench' },
    { id: 'movable', label: 'Movable Chairs', description: 'Flexible arrangement' },
    { id: 'combo', label: 'Combination', description: 'Built-in with accent chairs' },
  ],
};

// Fence selections
export const FENCE_MATERIAL_OPTIONS: SelectionCategory = {
  id: 'fence_material',
  label: 'Fence Material',
  options: [
    { id: 'wood', label: 'Wood', description: 'Cedar, redwood, or pine' },
    { id: 'vinyl', label: 'Vinyl', description: 'Maintenance-free privacy' },
    { id: 'metal', label: 'Metal', description: 'Aluminum or wrought iron' },
    { id: 'composite', label: 'Composite', description: 'Wood-look, long-lasting' },
    { id: 'chain', label: 'Chain Link', description: 'Economical, secure' },
  ],
};

export const FENCE_HEIGHT_OPTIONS: SelectionCategory = {
  id: 'fence_height',
  label: 'Fence Height',
  options: [
    { id: '4ft', label: '4 Feet', description: 'Decorative boundary' },
    { id: '6ft', label: '6 Feet', description: 'Standard privacy' },
    { id: '8ft', label: '8 Feet', description: 'Maximum privacy' },
  ],
};

export const GATE_OPTIONS: SelectionCategory = {
  id: 'gate_options',
  label: 'Gate Options',
  options: [
    { id: 'single', label: 'Single Gate', description: 'Standard walk-through' },
    { id: 'double', label: 'Double Gate', description: 'Wide access for equipment' },
    { id: 'sliding', label: 'Sliding Gate', description: 'Space-saving design' },
  ],
};

// Landscape selections
export const HARDSCAPE_OPTIONS: SelectionCategory = {
  id: 'hardscape_material',
  label: 'Hardscape Material',
  options: [
    { id: 'pavers', label: 'Pavers', description: 'Brick or stone pathways' },
    { id: 'gravel', label: 'Gravel', description: 'Decorative loose stone' },
    { id: 'flagstone', label: 'Flagstone', description: 'Natural stepping stones' },
    { id: 'concrete', label: 'Concrete', description: 'Poured or stamped' },
  ],
};

export const PLANTER_OPTIONS: SelectionCategory = {
  id: 'planter_style',
  label: 'Planter Style',
  options: [
    { id: 'raised', label: 'Raised Beds', description: 'Built-in planting areas' },
    { id: 'retaining', label: 'Retaining Walls', description: 'Terraced landscaping' },
    { id: 'containers', label: 'Container Garden', description: 'Movable planters' },
  ],
};

export const PATHWAY_LIGHTING_OPTIONS: SelectionCategory = {
  id: 'pathway_lighting',
  label: 'Pathway Lighting',
  options: [
    { id: 'solar', label: 'Solar Lights', description: 'Eco-friendly, easy install' },
    { id: 'low_voltage', label: 'Low Voltage', description: 'Brighter, wired system' },
    { id: 'in_ground', label: 'In-Ground', description: 'Flush-mounted fixtures' },
  ],
};

// ============================================
// DEFAULT OUTDOOR PROJECT TYPES
// ============================================

export const DEFAULT_OUTDOOR_PROJECT_TYPES: ProjectTypeConfig[] = [
  {
    id: 'deck',
    label: 'Deck',
    description: 'Wood or composite decking',
    selectionCategories: [
      DECK_MATERIAL_OPTIONS,
      RAILING_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'patio',
    label: 'Patio',
    description: 'Concrete, pavers, or stone',
    selectionCategories: [
      PATIO_SURFACE_OPTIONS,
      BORDER_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'pergola',
    label: 'Pergola',
    description: 'Shade structure with posts and beams',
    selectionCategories: [
      PERGOLA_STRUCTURE_OPTIONS,
      CANOPY_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'pool_area',
    label: 'Pool Area',
    description: 'Pool surrounds, decking, outdoor kitchen',
    selectionCategories: [
      POOL_DECKING_OPTIONS,
      POOL_FENCING_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'gazebo',
    label: 'Gazebo',
    description: 'Freestanding covered structure',
    selectionCategories: [
      GAZEBO_STRUCTURE_OPTIONS,
      SCREENING_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'outdoor_kitchen',
    label: 'Outdoor Kitchen',
    description: 'Built-in grill, counters, sink',
    selectionCategories: [
      OUTDOOR_COUNTERTOP_OPTIONS,
      APPLIANCE_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'fire_pit',
    label: 'Fire Pit Area',
    description: 'Fire pit with seating surrounds',
    selectionCategories: [
      FIRE_TYPE_OPTIONS,
      SEATING_OPTIONS,
      LIGHTING_OPTIONS,
    ],
  },
  {
    id: 'fence',
    label: 'Fence/Privacy',
    description: 'Fencing, lattice, privacy walls',
    selectionCategories: [
      FENCE_MATERIAL_OPTIONS,
      FENCE_HEIGHT_OPTIONS,
      GATE_OPTIONS,
    ],
  },
  {
    id: 'landscape',
    label: 'Landscape Refresh',
    description: 'Hardscaping, planters, pathways',
    selectionCategories: [
      HARDSCAPE_OPTIONS,
      PLANTER_OPTIONS,
      PATHWAY_LIGHTING_OPTIONS,
    ],
  },
];

/**
 * @deprecated Use getProjectTypes instead. Garage no longer has defaults.
 * Project types are now fully optional and configured per-tenant.
 */
export const DEFAULT_GARAGE_CONVERSIONS: RoomConversionOption[] = [
  { id: 'standard', label: 'Standard Refresh', description: 'Flooring, storage, organization' },
  { id: 'adu', label: 'Additional Living Space', description: 'Full ADU conversion' },
  { id: 'man_cave', label: 'Man Cave', description: 'Entertainment space' },
  { id: 'guest_suite', label: 'Guest Suite', description: 'Mother-in-law suite' },
  { id: 'home_gym', label: 'Home Gym', description: 'Fitness space' },
  { id: 'workshop', label: 'Workshop/Studio', description: 'Work and hobby space' },
];

// Get room types based on tenant config (with optional overrides)
export function getRoomTypes(roomConfig?: RoomConfig): RoomTypeOption[] {
  const defaultEnabled = ROOM_TYPES.map((r) => r.id);
  const enabled = roomConfig?.enabled_rooms ?? defaultEnabled;

  return ROOM_TYPES
    .filter((room) => enabled.includes(room.id))
    .map((room) => {
      const override = roomConfig?.room_overrides?.[room.id];
      if (override) {
        return {
          ...room,
          label: override.label ?? room.label,
          description: override.description ?? room.description,
        };
      }
      return room;
    });
}

/**
 * Get project types for a room/area type.
 * Returns the new projectTypes format, falling back to legacy conversion_options.
 * Outdoor has defaults; all other areas are optional (no defaults).
 */
export function getProjectTypes(
  roomType: RoomType,
  roomConfig?: RoomConfig
): ProjectTypeConfig[] | null {
  const override = roomConfig?.room_overrides?.[roomType];

  // Check for new projectTypes format first
  if (override?.projectTypes && override.projectTypes.length > 0) {
    return override.projectTypes;
  }

  // Fall back to legacy conversion_options (convert to ProjectTypeConfig)
  if (override?.conversion_options && override.conversion_options.length > 0) {
    return override.conversion_options.map((opt) => ({
      id: opt.id,
      label: opt.label,
      description: opt.description,
    }));
  }

  // Return defaults for outdoor only
  if (roomType === 'outdoor') {
    return DEFAULT_OUTDOOR_PROJECT_TYPES;
  }

  // No defaults for other areas (including garage - now optional)
  return null;
}

/**
 * @deprecated Use getProjectTypes instead.
 * Kept for backward compatibility.
 */
export function getConversionOptions(
  roomType: RoomType,
  roomConfig?: RoomConfig
): RoomConversionOption[] | null {
  const projectTypes = getProjectTypes(roomType, roomConfig);
  if (!projectTypes) return null;

  // Convert ProjectTypeConfig to RoomConversionOption for backward compatibility
  return projectTypes.map((pt) => ({
    id: pt.id,
    label: pt.label,
    description: pt.description || '',
  }));
}

/**
 * Get selection categories for a specific project type.
 * Used when rendering the SelectionsStep with project types selected.
 */
export function getSelectionsForProjectType(
  projectTypeId: string,
  roomType: RoomType,
  roomConfig?: RoomConfig
): SelectionCategory[] {
  const projectTypes = getProjectTypes(roomType, roomConfig);
  if (!projectTypes) return [];

  const projectType = projectTypes.find((pt) => pt.id === projectTypeId);
  if (!projectType?.selectionCategories) return [];

  // Convert SelectionCategoryConfig to SelectionCategory
  return projectType.selectionCategories.map((cat) => ({
    id: cat.id,
    label: cat.label,
    allowColor: cat.allowColor,
    options: cat.options.map((opt) => ({
      id: opt.id,
      label: opt.label,
      description: opt.description,
    })),
  }));
}

// Style presets for AI generation
export const STYLE_PRESETS = [
  { id: 'modern', label: 'Modern', description: 'Clean lines, minimal' },
  { id: 'traditional', label: 'Traditional', description: 'Classic, timeless' },
  { id: 'farmhouse', label: 'Farmhouse', description: 'Rustic, cozy' },
  { id: 'contemporary', label: 'Contemporary', description: 'Current trends' },
  { id: 'industrial', label: 'Industrial', description: 'Raw, urban feel' },
  { id: 'coastal', label: 'Coastal', description: 'Light, beachy vibes' },
];

// Popular color options
export const COLOR_PRESETS = [
  { id: 'white', label: 'White', hex: '#FFFFFF' },
  { id: 'cream', label: 'Cream', hex: '#FFFDD0' },
  { id: 'gray', label: 'Gray', hex: '#808080' },
  { id: 'navy', label: 'Navy', hex: '#000080' },
  { id: 'sage', label: 'Sage Green', hex: '#9DC183' },
  { id: 'black', label: 'Black', hex: '#000000' },
  { id: 'beige', label: 'Beige', hex: '#F5F5DC' },
  { id: 'blue', label: 'Blue', hex: '#4169E1' },
];
